import { useState, useEffect } from "react";
import { Users, RefreshCw, Download, Info, Video } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminRegistrationsPage() {
  const [key, setKey] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [tab, setTab] = useState("webinar");
  const [registrations, setRegistrations] = useState([]);
  const [infoRequests, setInfoRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async (apiKey) => {
    setLoading(true);
    setError("");
    const k = apiKey || key;
    try {
      const [regRes, infoRes] = await Promise.all([
        axios.get(`${API}/webinar-registrations?key=${k}`),
        axios.get(`${API}/info-requests?key=${k}`)
      ]);
      if (regRes.data.error) {
        setError(regRes.data.error);
        setAuthorized(false);
      } else {
        setRegistrations(regRes.data);
        setInfoRequests(infoRes.data.error ? [] : infoRes.data);
        setAuthorized(true);
      }
    } catch (err) {
      setError("Nepodařilo se načíst data.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => { e.preventDefault(); fetchData(); };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("cs-CZ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return dateStr; }
  };

  const formatWebinarDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("cs-CZ", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) + " " + d.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
    } catch { return dateStr; }
  };

  const exportCSV = (data, filename, headers, rowFn) => {
    const rows = data.map(rowFn);
    const csv = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#F4F7FC] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-[#E8EDF5] p-8 max-w-sm w-full">
          <div className="w-12 h-12 rounded-xl bg-[#0049B3]/10 flex items-center justify-center mx-auto mb-5">
            <Users className="w-6 h-6 text-[#0049B3]" />
          </div>
          <h1 className="text-xl font-bold text-[#0F1B3D] text-center mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Admin panel</h1>
          <p className="text-sm text-[#6B7A99] text-center mb-6">Zadejte přístupový klíč</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input type="password" placeholder="Přístupový klíč" value={key} onChange={(e) => setKey(e.target.value)} className="rounded-xl border-[#E8EDF5] py-5" />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl py-5 font-bold">
              {loading ? "Načítám..." : "Přihlásit se"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const currentData = tab === "webinar" ? registrations : infoRequests;

  return (
    <div className="min-h-screen bg-[#F4F7FC]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-[#0F1B3D]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Admin panel</h1>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-xl border-[#E8EDF5]" onClick={() => fetchData()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Obnovit
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab("webinar")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all ${
              tab === "webinar" ? "bg-[#0049B3] text-white shadow-lg shadow-blue-600/20" : "bg-white text-[#3D4F6F] border border-[#E8EDF5] hover:bg-[#F8FAFF]"
            }`}
          >
            <Video className="w-4 h-4" />
            Webinář ({registrations.length})
          </button>
          <button
            onClick={() => setTab("info")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all ${
              tab === "info" ? "bg-[#0049B3] text-white shadow-lg shadow-blue-600/20" : "bg-white text-[#3D4F6F] border border-[#E8EDF5] hover:bg-[#F8FAFF]"
            }`}
          >
            <Info className="w-4 h-4" />
            Více informací ({infoRequests.length})
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center">
            <div className="text-2xl font-extrabold text-[#0049B3]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{registrations.length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Webinář</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center">
            <div className="text-2xl font-extrabold text-[#FFC72C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{infoRequests.length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Více informací</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E8EDF5] p-4 text-center col-span-2">
            <div className="text-2xl font-extrabold text-emerald-600" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{registrations.length + infoRequests.length}</div>
            <div className="text-xs text-[#6B7A99] mt-1">Celkem kontaktů</div>
          </div>
        </div>

        {/* Export */}
        <div className="mb-4 flex justify-end">
          <Button className="bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl" onClick={() => {
            if (tab === "webinar") {
              exportCSV(registrations, `webinar-${new Date().toISOString().slice(0,10)}.csv`,
                ["Jméno","Email","Telefon","Škola/IČO","Typ","Termín","Operátor","Registrováno"],
                r => [r.name, r.email, r.phone||"", r.school_name||r.ico||"", r.school_type||"", formatWebinarDate(r.webinar_date), r.operator_id||"", formatDate(r.registered_at)]
              );
            } else {
              exportCSV(infoRequests, `info-requests-${new Date().toISOString().slice(0,10)}.csv`,
                ["Jméno","Email","Telefon","Škola","Zpráva","Datum"],
                r => [r.name, r.email, r.phone||"", r.school_name||"", r.message||"", formatDate(r.registered_at)]
              );
            }
          }}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#E8EDF5] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            {tab === "webinar" ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8EDF5] bg-[#F8FAFF]">
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">#</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Jméno</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Telefon</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Škola/IČO</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Typ</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Termín</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Op.</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-12 text-[#9CA3AF]">Zatím žádné registrace</td></tr>
                  ) : registrations.map((r, idx) => (
                    <tr key={r.id} className="border-b border-[#F0F2F5] hover:bg-[#F8FAFF] transition-colors">
                      <td className="px-4 py-4 text-sm text-[#9CA3AF]">{idx + 1}</td>
                      <td className="px-4 py-4 text-sm font-semibold text-[#0F1B3D]">{r.name}</td>
                      <td className="px-4 py-4 text-sm"><a href={`mailto:${r.email}`} className="text-[#0049B3] hover:underline">{r.email}</a></td>
                      <td className="px-4 py-4 text-sm">{r.phone ? <a href={`tel:${r.phone}`} className="text-[#0049B3] hover:underline">{r.phone}</a> : "—"}</td>
                      <td className="px-4 py-4 text-sm text-[#3D4F6F]">{r.school_name || r.ico || "—"}</td>
                      <td className="px-4 py-4 text-sm">{r.school_type ? <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.school_type === 'zs' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{r.school_type === 'zs' ? 'ZŠ' : 'MŠ'}</span> : "—"}</td>
                      <td className="px-4 py-4 text-sm text-[#3D4F6F]">{formatWebinarDate(r.webinar_date)}</td>
                      <td className="px-4 py-4 text-sm text-[#3D4F6F]">{r.operator_id || "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#9CA3AF]">{formatDate(r.registered_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8EDF5] bg-[#F8FAFF]">
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">#</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Jméno</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Telefon</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Škola</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Zpráva</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7A99] uppercase tracking-wider">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {infoRequests.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF]">Zatím žádné požadavky</td></tr>
                  ) : infoRequests.map((r, idx) => (
                    <tr key={r.id} className="border-b border-[#F0F2F5] hover:bg-[#F8FAFF] transition-colors">
                      <td className="px-5 py-4 text-sm text-[#9CA3AF]">{idx + 1}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-[#0F1B3D]">{r.name}</td>
                      <td className="px-5 py-4 text-sm"><a href={`mailto:${r.email}`} className="text-[#0049B3] hover:underline">{r.email}</a></td>
                      <td className="px-5 py-4 text-sm">{r.phone ? <a href={`tel:${r.phone}`} className="text-[#0049B3] hover:underline">{r.phone}</a> : "—"}</td>
                      <td className="px-5 py-4 text-sm text-[#3D4F6F]">{r.school_name || "—"}</td>
                      <td className="px-5 py-4 text-sm text-[#3D4F6F] max-w-md whitespace-pre-wrap break-words">{r.message || "—"}</td>
                      <td className="px-5 py-4 text-xs text-[#9CA3AF]">{formatDate(r.registered_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
